import type { ApiErrorBody, ApiIssue, ErrorCode } from "@minket/core";

/**
 * The console API client: `fetch` against `/api` on the console's own origin. The session is
 * the API's own `__Host-minket_session` cookie, sent by the browser, so nothing here handles
 * credentials. JSON in, JSON out, snake_case on the wire.
 *
 * A success body comes back as the type its endpoint declares (lib/endpoints.ts: `z.infer` of
 * core's schemas, imported as types only). It is not parsed at runtime: zod and the schemas
 * would cost every route about 33 KB gzipped of its 150 KB budget, and the API builds its
 * responses from the same schemas. A non-2xx body is core's `ApiErrorBody`; it becomes an
 * `ApiRequestError` with the code, status, retryable flag, request id and field issues.
 *
 * Pages call `apiQuery` and `apiRequest` (lib/query.ts) with an endpoint, not `request`.
 */

/** Where the console API lives: a same-origin path, because the API serves no CORS. */
export const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

/**
 * The console's CSRF header, named as `CONSOLE_HEADER` in apps/api/src/http/auth/access.ts.
 * The API refuses every state-changing request without it, and a cross-site page cannot add
 * a custom header without a preflight the API never answers. It goes on every request so no
 * call site can forget it; `endpoints.test.ts` keeps the name equal to the API's.
 */
export const CONSOLE_HEADER = "x-minket-console";
export const CONSOLE_HEADER_VALUE = "1";

export type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

/** Query string values; `undefined` leaves the parameter out. */
export type Search = Readonly<Record<string, string | number | boolean | undefined>>;

export interface RequestOptions {
  method?: Method;
  /** Sent as JSON. */
  body?: unknown;
  search?: Search;
  signal?: AbortSignal;
}

interface ApiRequestErrorInit {
  message: string;
  /** HTTP status, or 0 when no response arrived. */
  status: number;
  /** The platform error code, or null when the body was not an `ApiErrorBody`. */
  code: ErrorCode | null;
  retryable: boolean;
  requestId: string | null;
  issues?: readonly ApiIssue[];
  cause?: unknown;
}

/** The console API answered with an error, or did not answer at all (`status` 0). */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ErrorCode | null;
  readonly retryable: boolean;
  readonly requestId: string | null;
  readonly issues: readonly ApiIssue[];

  constructor(init: ApiRequestErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = "ApiRequestError";
    this.status = init.status;
    this.code = init.code;
    this.retryable = init.retryable;
    this.requestId = init.requestId;
    this.issues = init.issues ?? [];
  }
}

export function isApiRequestError(value: unknown): value is ApiRequestError {
  return value instanceof ApiRequestError;
}

/** No session: the request never reached a signed-in user. */
export function isUnauthorized(value: unknown): boolean {
  return isApiRequestError(value) && value.status === 401;
}

/**
 * The API does not serve this route (501): the console is newer than the API it talks to. Pages
 * show it as any other failure (`ErrorState`, with the API's sentence); a control whose endpoint
 * is missing can use this to dim itself with a reason.
 */
export function isNotImplemented(value: unknown): boolean {
  return isApiRequestError(value) && (value.status === 501 || value.code === "not_implemented");
}

/** Missing, or in another organization: the API answers 404 for both, never 403. */
export function isNotFound(value: unknown): boolean {
  return isApiRequestError(value) && value.status === 404;
}

/** Retry only what might fix itself: no answer at all, or an error the API marks retryable. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  return failureCount < 2 && (!isApiRequestError(error) || error.retryable);
}

export function withSearch(path: string, search?: Search): string {
  if (!search) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const query = params.toString();
  return query === "" ? path : `${path}?${query}`;
}

/**
 * One call to the console API. `T` is the endpoint's declared success body; an empty body
 * (204) comes back as `null`.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, search, signal } = options;
  const headers: Record<string, string> = {
    accept: "application/json",
    [CONSOLE_HEADER]: CONSOLE_HEADER_VALUE,
  };
  if (body !== undefined) headers["content-type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${withSearch(path, search)}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "same-origin",
      signal,
    });
  } catch (cause) {
    if (signal?.aborted) throw cause;
    throw new ApiRequestError({
      message: "The console API could not be reached. Check the connection and try again.",
      status: 0,
      code: null,
      retryable: true,
      requestId: null,
      cause,
    });
  }

  const requestId = response.headers.get("x-request-id");
  const payload = await readJson(response);
  if (!response.ok) throw toRequestError(response.status, payload, requestId);

  // The trust boundary: the API builds this body from the core schema the endpoint names.
  return payload as T;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === "") return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/** Builds the error for a non-2xx response from its decoded body and the response headers. */
export function toRequestError(
  status: number,
  payload: unknown,
  requestId: string | null,
): ApiRequestError {
  const body = parseErrorBody(payload);
  if (body) {
    return new ApiRequestError({
      message: body.error.message,
      status,
      code: body.error.code,
      retryable: body.error.retryable,
      requestId: body.error.request_id ?? requestId,
      issues: body.error.issues ?? [],
    });
  }
  return new ApiRequestError({
    message: `The console API answered with status ${status}. Try again in a moment.`,
    status,
    code: null,
    retryable: status >= 500,
    requestId,
  });
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseIssue(value: unknown): ApiIssue | null {
  if (!isRecord(value) || typeof value.path !== "string" || typeof value.message !== "string") {
    return null;
  }
  return { path: value.path, message: value.message };
}

/**
 * A code shaped as core's are. Checking it against core's error table would put the whole table
 * in every route's first load (1.6 KB gzipped) for a body only Minket's own API writes.
 */
function isCodeLike(value: unknown): value is ErrorCode {
  return typeof value === "string" && /^[a-z][a-z0-9_]*$/.test(value);
}

/** Narrows a decoded body to core's `ApiErrorBody`; anything else is null. */
export function parseErrorBody(value: unknown): ApiErrorBody | null {
  if (!isRecord(value) || !isRecord(value.error)) return null;
  const { code, message, retryable, request_id, issues } = value.error;
  if (!isCodeLike(code) || typeof message !== "string" || typeof retryable !== "boolean") {
    return null;
  }
  const parsedIssues = Array.isArray(issues)
    ? issues.map(parseIssue).filter((issue): issue is ApiIssue => issue !== null)
    : undefined;
  return {
    error: {
      code,
      message,
      retryable,
      ...(typeof request_id === "string" ? { request_id } : {}),
      ...(parsedIssues ? { issues: parsedIssues } : {}),
    },
  };
}

/**
 * The API's messages for one form field: a validation error's `issues` whose path is the
 * field or inside it (`"to"` matches `to` and `to.0`), for showing next to that field.
 */
export function fieldIssues(error: unknown, field: string): string[] {
  if (!isApiRequestError(error)) return [];
  return error.issues
    .filter((issue) => issue.path === field || issue.path.startsWith(`${field}.`))
    .map((issue) => issue.message);
}

/** What an error state shows: the API's own sentence and the request id to quote. */
export function describeError(error: unknown): { message: string; requestId: string | null } {
  if (isApiRequestError(error)) return { message: error.message, requestId: error.requestId };
  if (error instanceof Error && error.message !== "") {
    return { message: error.message, requestId: null };
  }
  return { message: "Something went wrong. Try again in a moment.", requestId: null };
}
